import React from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import {
    Accordion, AccordionSummary, Box,
    BoxProps,
    CardProps,
    Chip, ListItemIconProps,
    Link as MuiLink,
    LinkProps as MuiLinkProps,
} from '@material-ui/core';
import { Card, ListItemIconDense as StyledListItemIcon } from '../../../../ui-kit';
import { OrderEvent } from '../../../../server-types';
import { defineChipColor, defineDetailsColor, defineMilestoneColor } from './utils';
import { ChipProps, ExpansionPanelSummaryProps } from './types';

export const OrderCard = styled(({ state, ...other }: CardProps & { state: OrderEvent }) => <Card {...other} />)`
    ${({ theme, state }) => `
        display: flex;
        max-width: 100vw;
        && {
            background-color: ${defineMilestoneColor(theme, state)};
            color: ${theme.palette.getContrastText(defineMilestoneColor(theme, state))};
            padding: ${theme.spacing(3)}px 0;
        }
    `}
`;

export const Time = styled.span`
    ${({ theme }) => `
        margin: ${theme.spacing(1)}px;
    `}
`;

export const ListItemIconDense = styled((
    { orderStatus, ...other }: ListItemIconProps & {orderStatus: OrderEvent}
) => <StyledListItemIcon {...other} />)`
    ${({ theme, orderStatus }) => `
        && {
            color: ${theme.palette.getContrastText(defineMilestoneColor(theme, orderStatus))};
        }
    `}
`;

export const SpacedChip = styled(({ changeReason, ...other }: ChipProps) => <Chip {...other} />)`
    ${({ theme, changeReason }) => `
       && {
            margin: 0 ${theme.spacing(0.5)}px;
            background-color: ${defineChipColor(theme, changeReason)};
       }
    `}
`;

export const ExpansionPanelSummary = styled((
    { reason, ...other }: ExpansionPanelSummaryProps
) => (
    <AccordionSummary {...other} />
))`
    ${({ theme, reason }) => `
        && {
            background-color: ${defineDetailsColor(theme, reason)};
            border-radius: ${theme.shape.borderRadius}px;
            overflow: hidden;
            & > * {
                color: ${theme.palette.getContrastText(defineDetailsColor(theme, reason))};
            }
        }
    `}
`;

export const ExpansionPanel = styled(Accordion)`
    ${({ theme }) => `
        && {
            border-radius: ${theme.shape.borderRadius}px;
            :before {
                display: none;
            }
            :last-child {
                border-radius: ${theme.shape.borderRadius}px;
            }
        }
    `}
`;

export const MilestoneTitle = styled.span`
    ${({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        font-size: ${theme.typography.fontSize}px;
    `}
`;
export const DetailTitle = styled.span`
    ${({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        font-size: ${theme.typography.pxToRem(18)};
        display: inline-flex;
        align-items: center;
    `}
`;

export const MilestoneDetail = styled.span`
    ${({ theme }) => `
        font-size: ${theme.typography.pxToRem(18)};
    `}
`;

interface ItemsCountProps extends Omit<BoxProps, 'component' | 'display' | 'justifyContent' | 'alignItems'> {
    count?: number;
}

export const ItemsCount = styled(
    ({ count, ...other }: ItemsCountProps) => (
        <Box component='span' display='flex' justifyContent='center' alignItems='center' {...other}>
            {count}
        </Box>
    )
)`
    ${({ theme }) => `
        border: 1px solid ${theme.palette.primary.main};
        border-radius: 50%;
        color: ${theme.palette.primary.main};
        width: ${theme.spacing(5)}px;
        height: ${theme.spacing(5)}px;
    `}
`;

export const DetailsRowContainer = styled(
    (props: Omit<BoxProps, 'display' | 'flexGrow' | 'alignItems' | 'mb'>) => (
        <Box display='flex' flexGrow={1} alignItems='center' mb={3} {...props} />
    )
)``;

interface LinkProps extends MuiLinkProps< typeof RouterLink, { to: string }> {
    ml?: number;
    mr?: number;
}

export const Link = styled(
    ({ to, ml, mr, ref, ...other }: Omit<LinkProps, 'component'>) =>
        <MuiLink innerRef={ref} to={to} component={RouterLink} {...other} />
)`
    ${({ theme, ml, mr }) => `
        font-weight: ${theme.typography.fontWeightRegular};
        && {
            ${mr ? `margin-right: ${theme.spacing(mr)}px;`: ``}
            ${ml ? `margin-left: ${theme.spacing(ml)}px;`: ``}
        }
    `}
`;
